import { useState } from "react";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";
import { usePageTitle } from "../../lib/usePageTitle";

type Integration = {
  name: string;
  description: string;
  icon: string;
  comingSoon?: boolean;
  active?: boolean;
};

const integrations: Integration[] = [
  {
    name: "Google Calendar",
    description: "Schedule appointments",
    icon: "ri-calendar-line",
  },
  {
    name: "Microsoft Outlook",
    description: "Sync emails and calendar events",
    icon: "ri-mail-line",
  },
  {
    name: "HubSpot",
    description: "Sync contacts and deals",
    icon: "ri-contacts-book-line",
  },
  {
    name: "Salesforce",
    description: "CRM sync",
    icon: "ri-cloud-line",
  },
  {
    name: "Twilio",
    description: "Voice and SMS",
    icon: "ri-message-2-line",
    active: true,
  },
  {
    name: "ElevenLabs",
    description: "Voice customization",
    icon: "ri-mic-line",
    active: true,
  },
  {
    name: "Zapier",
    description: "Connect 7,000+ apps",
    icon: "ri-flow-chart",
    comingSoon: true,
  },
  {
    name: "Epic EHR",
    description: "Healthcare records",
    icon: "ri-heart-pulse-line",
    comingSoon: true,
  },
  {
    name: "Stripe",
    description: "Billing and payments",
    icon: "ri-bank-card-line",
    comingSoon: true,
  },
];

function IntegrationCard({
  integration,
  isNotified,
  onNotify,
}: {
  integration: Integration;
  isNotified: boolean;
  onNotify: () => void;
}) {
  return (
    <section
      className={`rounded-xl backdrop-blur-sm overflow-hidden ${cardBorderClassName}`}
      style={{
        background: "rgba(28, 28, 37, 0.8)",
      }}
    >
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <i className={`${integration.icon} text-purple-400 text-2xl mt-1`} />
          <div>
            <h2
              className="text-xl font-semibold text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {integration.name}
            </h2>
            <p
              className="text-gray-400 text-sm mt-1"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {integration.description}
            </p>
          </div>
        </div>

        <div className="mt-auto pt-2">
          {integration.active ? (
            <span
              className="inline-flex px-4 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: "rgba(34,197,94,0.15)",
                border: "1px solid rgba(34,197,94,0.3)",
                color: "rgb(134,239,172)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              Active
            </span>
          ) : isNotified ? (
            <span
              className="text-sm font-semibold text-green-400"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              ✓ We&apos;ll let you know!
            </span>
          ) : (
            <button
              type="button"
              onClick={onNotify}
              className="inline-flex px-4 py-2 rounded-lg text-sm font-semibold text-purple-200 cursor-pointer hover:border-purple-400/40 transition-colors"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(138, 43, 226, 0.2)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              🔔 Notify me
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Integrations() {
  usePageTitle("Integrations");
  const [notified, setNotified] = useState<Set<string>>(new Set());

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          title="Integrations"
          subtitle="Connect your AI to the tools you already use."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {integrations.map((integration) => (
            <IntegrationCard
              key={integration.name}
              integration={integration}
              isNotified={notified.has(integration.name)}
              onNotify={() =>
                setNotified((prev) => new Set(prev).add(integration.name))
              }
            />
          ))}
        </div>
      </PageContainer>
    </CoreLayout>
  );
}
